import { openDialog } from '@patrianna/dialog-manager'
import { gateway, gatewayError } from '@patrianna/gateway'
import gatewayConfig from '@patrianna/gateway/config'
import { isBrowser } from '@patrianna/shared-utils/helpers'

import { logErrorToSentry } from 'app/utils/logErrorToSentry'
import { openSnackbar } from 'store/modules/snackbar/actions'
import { createApiClient } from 'temp/api'
import { createApiClient as createFraudApiClient } from 'temp/fraud'
import { createApiClient as createPaymentApiClient } from 'temp/payment'

import type { ValidateResponse } from 'temp/api/types'

// import type { ConfigsProps, ExtendedBodyProps } from '@patrianna/gateway/types'
// import createHmac from 'utils/createHmac'

// New Dialog Manager migration adapter
const openDialogAdapted =
  <OpenDialogPayload extends Parameters<typeof openDialog>[0]>(payload: OpenDialogPayload) =>
  () => {
    if (!Array.isArray(payload)) {
      // @ts-ignore
      const { dialogName, modalName, ...props } = payload

      openDialog({
        dialogName: dialogName || modalName,
        ...props,
      })
    } else {
      openDialog(payload)
    }
  }

const gatewayInstance = gateway.create({
  baseURL: gatewayConfig.get('GATEWAY'),
  baseFetchURL: process.env.SERVER_FETCH_URL,
  brandName: process.env.BRAND_NAME,
  updatedTimestamp: true,
  /* extendedBodyHook: (extendedBody, configs) => ({
    ...extendedBody,
    headers: {
      ...extendedBody.headers,
      chk: createHmac(extendedBody, configs?.platform as 'web'),
    },
  }),
  addingHeadersByBodyRequestHook: ({ extendedBody, configs }) => ({
    'X-Timestamp': Date.now().toString(),
    'X-Platform': configs?.platform,
    'X-Chk': createHmac(extendedBody, configs?.platform as 'web'),
  }), */
})

export const gatewayErrorInstance = gatewayError.setupActions({ openDialog: openDialogAdapted, openSnackbar })

export default gatewayInstance

export const api = createApiClient({
  url: isBrowser() ? process.env.GATEWAY : process.env.SERVER_FETCH_URL,
  brandName: process.env.BRAND_NAME,
  platform: 'web',
  urlSigningsKey: process.env.URL_SIGNING_KEY,
  userAgent: isBrowser() ? undefined : process.env.VERCEL_USER_AGENT || 'VERCEL_USER_AGENT undefined',
  onValidationError: (payload: ValidateResponse) => {
    if (process.env.PROFILE === 'dev') {
      console.warn(`[API VALIDATION ERROR]: ${payload?.request}`, payload?.errors)
    }

    const error = new Error(`API validation error for ${payload?.request}`)
    logErrorToSentry(error, 'http_500', {
      pathname: payload?.request,
      platform: process.env.BRAND_NAME === 'pulsz' ? 'pulsz' : 'bingo',
    })
  },
})

export const fraud = createFraudApiClient({
  url: isBrowser() ? process.env.FRAUD_GATEWAY : process.env.FRAUD_SERVER_FETCH_URL,
  brandName: process.env.BRAND_NAME,
  platform: 'web',
  urlSigningsKey: process.env.URL_SIGNING_KEY,
  userAgent: isBrowser() ? undefined : process.env.VERCEL_USER_AGENT || 'VERCEL_USER_AGENT undefined',
  onValidationError: (payload: ValidateResponse) => {
    if (process.env.PROFILE === 'dev') {
      console.warn(`[API VALIDATION ERROR]: ${payload?.request}`, payload?.errors)
    }

    const error = new Error(`Fraud API validation error for ${payload?.request}`)
    logErrorToSentry(error, 'http_500', {
      pathname: payload?.request,
      platform: process.env.BRAND_NAME === 'pulsz' ? 'pulsz' : 'bingo',
    })
  },
})

export const payment = createPaymentApiClient({
  url: isBrowser() ? process.env.PAYMENT_GATEWAY : process.env.PAYMENT_SERVER_FETCH_URL,
  brandName: process.env.BRAND_NAME,
  platform: 'web',
  urlSigningsKey: process.env.URL_SIGNING_KEY,
  userAgent: isBrowser() ? undefined : process.env.VERCEL_USER_AGENT || 'VERCEL_USER_AGENT undefined',
  encryptedMethods: process.env.PROFILE !== 'dev' ? ['GetPaymentServiceInfoRequest'] : [],
  onValidationError: (payload: ValidateResponse) => {
    if (process.env.PROFILE === 'dev') {
      console.warn(`[API VALIDATION ERROR]: ${payload?.request}`, payload?.errors)
    }

    const error = new Error(`Payment API validation error for ${payload?.request}`)
    logErrorToSentry(error, 'http_500', {
      pathname: payload?.request,
      platform: process.env.BRAND_NAME === 'pulsz' ? 'pulsz' : 'bingo',
    })
  },
})
